// VideoInterviewPage.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as faceapi from 'face-api.js';
import { backend } from './index';

const VideoInterviewPage = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();

  // ── Refs ────────────────────────────────────────────────────────
  const videoRef = useRef(null);
  const expressionLog = useRef([]);
  const intervalRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const confettiCanvasRef = useRef(null);

  // ── State ───────────────────────────────────────────────────────
  const [phase, setPhase] = useState('setup');
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [camReady, setCamReady] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentOptions, setCurrentOptions] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const [feedback, setFeedback] = useState('');
  const [expressions, setExpressions] = useState({});
  const [questionCount, setQuestionCount] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [history, setHistory] = useState([]);
  const [dominantEmotion, setDominantEmotion] = useState('');
  const [error, setError] = useState('');
  // Trigger to animate confidence bars after done renders
  const [confBarsAnimated, setConfBarsAnimated] = useState(false);

  const MAX_QUESTIONS = 5;
  const token = localStorage.getItem('token');

  const emotionEmoji = {
    happy: '😊', sad: '😢', angry: '😠',
    fearful: '😨', disgusted: '🤢', surprised: '😲', neutral: '😐'
  };

  // ── Auth fetch ──────────────────────────────────────────────────
  const authFetch = (url, options = {}) =>
    fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

  // ── Load face-api models ────────────────────────────────────────
  useEffect(() => {
    const loadModels = async () => {
      try {
        setStatusMsg('Loading AI models...');
        await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
        await faceapi.nets.faceExpressionNet.loadFromUri('/models');
        setModelsLoaded(true);
        setStatusMsg('Models ready ✓');
      } catch (e) {
        console.warn('face-api models failed to load:', e);
        setModelsLoaded(true);
        setStatusMsg('Ready (expression detection unavailable)');
      }
    };
    loadModels();
  }, []);

  // ── Confetti effect — fires when phase becomes 'done' ──────────
  useEffect(() => {
    if (phase !== 'done') return;

    // Trigger confidence bar animation after a short delay
    const barTimer = setTimeout(() => setConfBarsAnimated(true), 200);

    const canvas = confettiCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    const COLORS = ['#fbbf24','#f87171','#a78bfa','#34d399','#60a5fa','#f472b6','#fb923c'];
    const SHAPES = ['rect', 'circle', 'twist'];

    class Piece {
      constructor(delay) {
        this.delay = delay;
        this.reset(true);
      }
      reset(initial) {
        this.x = Math.random() * canvas.width;
        this.y = initial ? Math.random() * -canvas.height * 2 : -20;
        this.size = 6 + Math.random() * 10;
        this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
        this.speed = 2 + Math.random() * 3;
        this.angle = Math.random() * Math.PI * 2;
        this.spin = (Math.random() - 0.5) * 0.18;
        this.drift = (Math.random() - 0.5) * 1.4;
        this.shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
        this.opacity = 0.7 + Math.random() * 0.3;
      }
      update() {
        if (this.delay > 0) { this.delay--; return; }
        this.y += this.speed;
        this.x += this.drift;
        this.angle += this.spin;
        if (this.y > canvas.height + 30) this.reset(false);
      }
      draw() {
        if (this.delay > 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle);
        ctx.globalAlpha = this.opacity;
        ctx.fillStyle = this.color;
        if (this.shape === 'rect') {
          ctx.fillRect(-this.size / 2, -this.size / 4, this.size, this.size / 2);
        } else if (this.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, this.size / 2.5, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.moveTo(-this.size / 2, 0);
          ctx.bezierCurveTo(-this.size / 4, -this.size / 3, this.size / 4, this.size / 3, this.size / 2, 0);
          ctx.strokeStyle = this.color;
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }
        ctx.restore();
      }
    }

    const pieces = [];
    for (let i = 0; i < 180; i++) {
      pieces.push(new Piece(Math.floor(i * 2)));
    }

    let frame = 0;
    let raf;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => { p.update(); p.draw(); });
      frame++;
      if (frame < 420) raf = requestAnimationFrame(animate);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(barTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [phase]);

  // ── Start camera ────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      videoRef.current.srcObject = stream;
      setCamReady(true);
      setStatusMsg('Camera ready ✓');
    } catch (e) {
      setError('Camera/mic access denied. Please allow access and refresh.');
    }
  };

  // ── Expression sampling loop ────────────────────────────────────
  const startExpressionLoop = () => {
    intervalRef.current = setInterval(async () => {
      if (!videoRef.current) return;
      if (!faceapi.nets.tinyFaceDetector.isLoaded ||
          !faceapi.nets.faceExpressionNet.isLoaded) return;
      try {
        const det = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
          .withFaceExpressions();
        if (det) {
          const exp = det.expressions;
          expressionLog.current.push({ t: Date.now(), ...exp });
          const dominant = Object.entries(exp).sort((a, b) => b[1] - a[1])[0];
          setDominantEmotion(dominant ? dominant[0] : '');
          setExpressions({ ...exp });
        }
      } catch (err) {
        console.warn('Expression detection error:', err);
      }
    }, 2000);
  };

  // ── Bot speaks ──────────────────────────────────────────────────
  // ✅ FIX: Added onerror handler so fetchQuestion always fires even if speech fails
  // ✅ FIX: Wait for voices to load before speaking (Chrome async voices bug)
const botSpeak = useCallback((text, onDone) => {
  synthRef.current.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.rate = 0.95;
  utter.pitch = 1.0;

  const assignVoiceAndSpeak = () => {
    // 1. Get all available voices
    const voices = synthRef.current.getVoices();
    
    // 2. Filter for English ONLY (prevents the German bug)
    const englishVoices = voices.filter(v => v.lang.startsWith('en'));

    // 3. Select preferred accent/quality
    // Priority: Google Natural -> Premium (Samantha/Daniel) -> Any US English
    const preferred = 
      englishVoices.find(v => v.name.includes('Google US English')) ||
      englishVoices.find(v => v.name.includes('Google')) ||
      englishVoices.find(v => v.name.includes('Samantha')) ||
      englishVoices.find(v => v.lang === 'en-US') ||
      englishVoices[0]; // Fallback to first English voice

    if (preferred) utter.voice = preferred;

    utter.onstart = () => setIsBotSpeaking(true);
    utter.onend = () => { setIsBotSpeaking(false); if (onDone) onDone(); };
    utter.onerror = () => { setIsBotSpeaking(false); if (onDone) onDone(); };
    
    synthRef.current.speak(utter);
  };

  // Chrome loads voices asynchronously
  const voices = synthRef.current.getVoices();
  if (voices.length > 0) {
    assignVoiceAndSpeak();
  } else {
    synthRef.current.onvoiceschanged = assignVoiceAndSpeak;
  }
}, []);
  // ── Web Speech Recognition ──────────────────────────────────────
  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Speech recognition not supported. Please use Chrome.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (e) => {
      if (e.error !== 'no-speech') setError(`Speech error: ${e.error}`);
    };
    recognition.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      if (final) setTranscript(prev => prev + ' ' + final);
      setInterimText(interim);
    };
    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimText('');
  };

  // ── Fetch question ──────────────────────────────────────────────
  const fetchQuestion = async () => {
    setIsLoading(true);
    setStatusMsg('Getting next question...');
    setSelectedOption(null);
    setCurrentOptions(null);
    setCorrectAnswer(null);
    setTranscript('');
    setInterimText('');
    setFeedback('');

    try {
      const res = await authFetch(`${backend}/get-question`, {
        method: 'POST',
        body: JSON.stringify({ section: sectionId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to get question');

      setCurrentQuestion(data.question);
      setCurrentOptions(data.options || null);
      setCorrectAnswer(data.correct || null);
      setIsLoading(false);
      setStatusMsg('');

      let spokenText = data.question;
      if (data.options) {
        spokenText += ` ... The options are ...
          Option A: ${data.options.A} ...
          Option B: ${data.options.B} ...
          Option C: ${data.options.C} ...
          Option D: ${data.options.D} ...
          Please say A, B, C, or D, or explain your answer.`;
      }

      botSpeak(spokenText, () => {
        setStatusMsg('🎤 Your turn — speak your answer or click an option');
        startListening();
      });
    } catch (e) {
      setError(e.message);
      setIsLoading(false);
    }
  };

  // ── Submit answer ───────────────────────────────────────────────
  const submitAnswer = async () => {
    const finalAnswer = selectedOption
      ? `${selectedOption}: ${currentOptions[selectedOption]}`
      : transcript.trim();

    if (!finalAnswer) {
      setStatusMsg('⚠️ Please speak your answer or select an option.');
      return;
    }
    stopListening();
    setIsLoading(true);
    setStatusMsg('AI is evaluating your answer...');

    const log = expressionLog.current;
    const avgExp = {};
    if (log.length > 0) {
      Object.keys(log[0]).filter(k => k !== 't').forEach(k => {
        avgExp[k] = log.reduce((s, d) => s + (d[k] || 0), 0) / log.length;
      });
    }

    try {
      const res = await authFetch(`${backend}/video-answer`, {
        method: 'POST',
        body: JSON.stringify({
          question: currentQuestion,
          transcript: finalAnswer,
          expression_log: log,
          section_id: sectionId,
        }),
      });
      const data = await res.json();
      setFeedback(data.feedback);
      setHistory(prev => [...prev, {
        question: currentQuestion,
        transcript: finalAnswer,
        feedback: data.feedback,
        expressions: avgExp,
        confidence: data.confidence_score,
        options: currentOptions,
        selected: selectedOption,
        correct: correctAnswer,
      }]);
      expressionLog.current = [];
      setIsLoading(false);
      setStatusMsg('');

      botSpeak(`Here is my feedback. ${data.feedback}`, () => {
        if (questionCount + 1 >= MAX_QUESTIONS) {
          setPhase('done');
        }
      });
      setQuestionCount(prev => prev + 1);
    } catch (e) {
      setError(e.message);
      setIsLoading(false);
    }
  };

  // ── Start interview ─────────────────────────────────────────────
  const startInterview = () => {
    setPhase('interview');
    startExpressionLoop();

    authFetch(`${backend}/select-company`, {
      method: 'POST',
      body: JSON.stringify({ company: 'TCS' }),
    }).catch(e => console.warn('select-company failed (non-blocking):', e));

    botSpeak(
      `Hello! Welcome to your ${sectionId.replace(/_/g, ' ')} interview. I will ask you ${MAX_QUESTIONS} questions. Please speak clearly after each question. Let's begin!`,
      () => fetchQuestion()
    );
  };

  // ── Cleanup ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      stopListening();
      synthRef.current.cancel();
      if (videoRef.current?.srcObject)
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ── Emotion confidence calculator ────────────────────────────────
  const calcEmotionConfidence = (avgExpressions) => {
    if (!avgExpressions || Object.keys(avgExpressions).length === 0) return null;
    const positive = (avgExpressions.happy || 0) + (avgExpressions.neutral || 0) * 0.5;
    const negative = (avgExpressions.fearful || 0) + (avgExpressions.sad || 0)
                   + (avgExpressions.angry || 0) + (avgExpressions.disgusted || 0);
    const raw = 0.4 + positive * 0.5 - negative * 0.45;
    return Math.min(1, Math.max(0.1, raw));
  };

  // ── Compute overall emotion confidence from all history ──────────
  const computeOverallExpressions = () => {
    const overall = {};
    let count = 0;
    history.forEach(h => {
      if (h.expressions && Object.keys(h.expressions).length > 0) {
        Object.entries(h.expressions).forEach(([k, v]) => {
          overall[k] = (overall[k] || 0) + v;
        });
        count++;
      }
    });
    if (count > 0) {
      Object.keys(overall).forEach(k => { overall[k] /= count; });
    }
    return { overall, count };
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div className="video-interview-page" style={S.page}>
      {/* ✅ FIX: canvas is now properly inside JSX, not floating in component body */}
      <canvas ref={confettiCanvasRef} style={S.confettiCanvas} />

      {/* Ribbons — shown only on done phase, matching HTML reference exactly */}
      {phase === 'done' && (
        <div style={S.ribbonContainer}>
          <div style={{ ...S.ribbon, width: 280, top: '18%', left: -280, background: 'linear-gradient(90deg,#f59e0b,#ef4444,#ec4899)', animation: 'ribbonWave 3.2s 0.3s forwards' }} />
          <div style={{ ...S.ribbon, width: 220, top: '35%', left: -220, background: 'linear-gradient(90deg,#22c55e,#3b82f6,#8b5cf6)', animation: 'ribbonWave 2.8s 0.8s forwards' }} />
          <div style={{ ...S.ribbon, width: 200, top: '60%', left: -200, background: 'linear-gradient(90deg,#ec4899,#f59e0b)', animation: 'ribbonWave2 3.5s 0.5s forwards' }} />
          <div style={{ ...S.ribbon, width: 260, top: '75%', left: -260, background: 'linear-gradient(90deg,#6366f1,#22c55e,#f59e0b)', animation: 'ribbonWave 3.0s 1.2s forwards' }} />
          <div style={{ ...S.ribbon, width: 180, top: '50%', left: -180, background: 'linear-gradient(90deg,#ef4444,#8b5cf6)', animation: 'ribbonWave2 2.6s 1.6s forwards' }} />
        </div>
      )}

      {/* ── Top bar ── */}
      <div style={S.topbar}>
        <div>
          <div style={S.topTitle}>🎥 AI Mock Interview</div>
          <div style={S.topSub}>{sectionId?.replace(/_/g, ' ').toUpperCase()} · Question {questionCount}/{MAX_QUESTIONS}</div>
        </div>
        <button style={S.exitBtn} onClick={() => { synthRef.current.cancel(); navigate('/home'); }}>
          ✕ Exit
        </button>
      </div>

      {error && <div style={S.errorBanner}>⚠️ {error}</div>}

      <div style={S.body}>

        {/* ── Left: Camera + expressions ── */}
        <div style={S.leftCol}>
          <div style={S.camBox}>
            <video ref={videoRef} autoPlay muted style={S.video} />
            {!camReady && (
              <div style={S.camOverlay}>
                <div style={{ fontSize: 40 }}>📷</div>
                <div style={{ marginTop: 8, fontSize: 14 }}>Camera not started</div>
              </div>
            )}
            {isBotSpeaking && <div style={S.botSpeakingBadge}>🤖 AI Speaking...</div>}
            {isListening && <div style={S.listeningBadge}>🔴 Listening</div>}
          </div>

          {camReady && Object.keys(expressions).length > 0 && (
            <div style={S.expressionBox}>
              <div style={S.expressionTitle}>Live Expression</div>
              <div style={S.dominantEmotionText}>
                {emotionEmoji[dominantEmotion] || '😐'} {dominantEmotion}
              </div>
              {Object.entries(expressions).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([k, v]) => (
                <div key={k} style={S.expRow}>
                  <span style={S.expLabel}>{k}</span>
                  <div style={S.expTrack}>
                    <div style={{ ...S.expFill, width: `${Math.round(v * 100)}%`, background: k === dominantEmotion ? '#22c55e' : '#3b82f6' }} />
                  </div>
                  <span style={S.expPct}>{Math.round(v * 100)}%</span>
                </div>
              ))}
            </div>
          )}

          {phase === 'interview' && (
            <div style={S.progressBox}>
              <div style={S.progressLabel}>Progress: {questionCount}/{MAX_QUESTIONS}</div>
              <div style={S.progressTrack}>
                <div style={{ ...S.progressFill, width: `${(questionCount / MAX_QUESTIONS) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Interview panel ── */}
        <div style={S.rightCol}>

          {/* SETUP PHASE */}
          {phase === 'setup' && (
            <div style={S.setupCard}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
              <h2 style={S.setupTitle}>AI Interviewer Ready</h2>
              <p style={S.setupDesc}>
                Your AI interviewer will ask you <strong>{MAX_QUESTIONS} questions</strong> based on your resume and selected section.<br /><br />
                It will <strong>speak each question aloud</strong>, listen to your spoken answer, and give you instant feedback.
              </p>
              <div style={S.setupSteps}>
                <div style={S.setupStep}>1. Click <strong>Enable Camera & Mic</strong></div>
                <div style={S.setupStep}>2. Allow browser permissions</div>
                <div style={S.setupStep}>3. Click <strong>Start Interview</strong></div>
                <div style={S.setupStep}>4. Listen to the question, then speak your answer or click an option</div>
                <div style={S.setupStep}>5. Click <strong>Submit Answer</strong> when done</div>
              </div>
              {!camReady ? (
                <button style={S.primaryBtn} onClick={startCamera}>📷 Enable Camera & Mic</button>
              ) : (
                <button style={{ ...S.primaryBtn, background: 'linear-gradient(135deg,#16a34a,#15803d)' }} onClick={startInterview}>
                  🚀 Start Interview
                </button>
              )}
              {statusMsg && <div style={S.statusMsg}>{statusMsg}</div>}
            </div>
          )}

          {/* INTERVIEW PHASE */}
          {phase === 'interview' && (
            <>
              <div style={S.botCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ ...S.botAvatar, animation: isBotSpeaking ? 'pulse 1s infinite' : 'none' }}>🤖</div>
                  <div>
                    <div style={S.botName}>AI Interviewer</div>
                    <div style={S.botStatus}>
                      {isBotSpeaking ? '🔊 Speaking...' : isListening ? '👂 Listening to you' : '⏳ Processing'}
                    </div>
                  </div>
                </div>

                {isLoading && !currentQuestion ? (
                  <div style={S.loadingText}>Getting your question...</div>
                ) : (
                  <div style={S.questionBox}>
                    <div style={S.questionLabel}>Question {questionCount + 1}</div>
                    <div style={S.questionText}>{currentQuestion}</div>

                    {currentOptions && !feedback && (
                      <div style={{ marginTop: 20 }}>
                        <div style={S.optionsLabel}>Select or speak your answer (A, B, C, or D):</div>
                        <div style={S.optionsGrid}>
                          {Object.entries(currentOptions).map(([key, val]) => (
                            <button
                              key={key}
                              style={{ ...S.optionBtn, ...(selectedOption === key ? S.optionBtnSelected : {}) }}
                              onClick={() => setSelectedOption(key)}
                            >
                              <span style={S.optionKey}>{key}.</span> {val}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentOptions && feedback && (
                      <div style={{ marginTop: 16 }}>
                        <div style={S.optionsLabel}>Answer review:</div>
                        {Object.entries(currentOptions).map(([key, val]) => (
                          <div key={key} style={{
                            ...S.optionBtn, marginBottom: 8, cursor: 'default',
                            background: key === correctAnswer ? 'rgba(34,197,94,0.15)' : key === selectedOption && selectedOption !== correctAnswer ? 'rgba(239,68,68,0.15)' : 'transparent',
                            border: key === correctAnswer ? '1px solid #22c55e' : key === selectedOption && selectedOption !== correctAnswer ? '1px solid #ef4444' : '1px solid #2a2840',
                          }}>
                            <span style={S.optionKey}>{key}.</span> {val}
                            {key === correctAnswer && <span style={{ color: '#22c55e', marginLeft: 8, fontWeight: 700 }}>✓ Correct</span>}
                            {key === selectedOption && selectedOption !== correctAnswer && <span style={{ color: '#ef4444', marginLeft: 8, fontWeight: 700 }}>✗ Your answer</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={S.transcriptCard}>
                <div style={S.transcriptLabel}>
                  🎤 Your Answer
                  {isListening && <span style={S.liveBadge}>LIVE</span>}
                </div>
                <div style={S.transcriptBox}>
                  {selectedOption && currentOptions && (
                    <span style={{ color: '#4f8ef7', fontWeight: 600 }}>Selected: {selectedOption}. {currentOptions[selectedOption]}</span>
                  )}
                  {!selectedOption && transcript && <span style={{ color: '#e8e6f0' }}>{transcript}</span>}
                  {!selectedOption && interimText && <span style={{ color: '#6b6888', fontStyle: 'italic' }}> {interimText}</span>}
                  {!selectedOption && !transcript && !interimText && (
                    <span style={{ color: '#4a4868' }}>
                      {isBotSpeaking ? 'Wait for the question to finish...' : currentOptions ? 'Click an option above, or speak A, B, C, or D...' : 'Start speaking after the question...'}
                    </span>
                  )}
                </div>
                <div style={S.transcriptActions}>
                  {!feedback && (
                    <>
                      {isListening ? (
                        <button style={S.stopBtn} onClick={stopListening}>⏹ Stop Recording</button>
                      ) : (
                        !isBotSpeaking && currentQuestion && (
                          <button style={S.micBtn} onClick={startListening}>🎤 {currentOptions ? 'Speak Instead' : 'Resume Recording'}</button>
                        )
                      )}
                      {(selectedOption || (!isListening && transcript)) && (
                        <button style={S.submitBtn} onClick={submitAnswer} disabled={isLoading}>
                          {isLoading ? 'Evaluating...' : '✓ Submit Answer'}
                        </button>
                      )}
                      {(selectedOption || transcript) && (
                        <button style={S.clearBtn} onClick={() => { setTranscript(''); setInterimText(''); setSelectedOption(null); }}>
                          🗑 Clear
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {feedback && (
                <div style={S.feedbackCard}>
                  <div style={S.feedbackLabel}>🤖 AI Feedback</div>
                  <div style={S.feedbackText}>{feedback}</div>
                  {questionCount < MAX_QUESTIONS ? (
                    <button style={S.nextBtn} onClick={() => { synthRef.current.cancel(); fetchQuestion(); }} disabled={isLoading}>
                      {isLoading ? 'Loading...' : 'Next Question →'}
                    </button>
                  ) : (
                    <button style={{ ...S.nextBtn, background: 'linear-gradient(135deg,#16a34a,#15803d)' }}
                      onClick={() => { synthRef.current.cancel(); setPhase('done'); }}>
                      View Summary →
                    </button>
                  )}
                </div>
              )}

              {statusMsg && <div style={S.statusMsg}>{statusMsg}</div>}
            </>
          )}

          {/* DONE PHASE — matches HTML reference exactly */}
          {phase === 'done' && (() => {
            const { overall: overallExpressions } = computeOverallExpressions();
            const emotionConfidence = calcEmotionConfidence(overallExpressions);
            const dominantOverall = Object.entries(overallExpressions).sort((a, b) => b[1] - a[1])[0];
            const hasExpressionData = Object.keys(overallExpressions).length > 0;
            const correctCount = history.filter(h => h.selected === h.correct).length;
            const avgConf = history.length > 0
              ? Math.round(history.reduce((s, h) => s + (h.confidence || 0), 0) / history.length * 100)
              : 0;

            return (
              <div style={S.doneWrapper}>

                {/* ── Trophy ring — 120×120 with pop animation and glow ── */}
                <div style={S.trophyRing}>
                  <svg width="120" height="120" viewBox="0 0 120 120" style={{ position: 'absolute', top: 0, left: 0, animation: 'spin 4s linear infinite' }}>
                    <circle cx="60" cy="60" r="54" fill="none" stroke="url(#rg2)" strokeWidth="4" strokeDasharray="20 8" strokeLinecap="round"/>
                    <defs>
                      <linearGradient id="rg2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fbbf24"/>
                        <stop offset="50%" stopColor="#f87171"/>
                        <stop offset="100%" stopColor="#a78bfa"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  <div style={S.trophyEmoji}>🎉</div>
                </div>

                {/* ── Title ── */}
                <div style={S.doneTitle}>Interview Complete!</div>
                <div style={S.doneSub}>You crushed it — here's your full performance breakdown</div>

                {/* ── 3 score cards ── */}
                <div style={S.scoreGrid}>
                  <div style={{ ...S.scoreCard, animationDelay: '0s' }}>
                    <div style={S.scoreLabel}>QUESTIONS</div>
                    <div style={{ ...S.scoreNum, color: '#4f8ef7', animationDelay: '1.2s' }}>{history.length}</div>
                  </div>
                  <div style={{ ...S.scoreCard, animationDelay: '0.5s' }}>
                    <div style={S.scoreLabel}>CORRECT</div>
                    <div style={{ ...S.scoreNum, color: '#22c55e', animationDelay: '1.2s' }}>{correctCount}</div>
                  </div>
                  <div style={{ ...S.scoreCard, animationDelay: '1s' }}>
                    <div style={S.scoreLabel}>AVG. CONFIDENCE</div>
                    <div style={{ ...S.scoreNum, color: '#f59e0b', animationDelay: '1.2s' }}>{avgConf}%</div>
                  </div>
                </div>

                {/* ── Question review ── */}
                <div style={S.historySection}>
                  <div style={S.historyLabel}>QUESTION REVIEW</div>
                  {history.map((h, i) => {
                    const isCorrect = h.selected === h.correct;
                    const qConf = calcEmotionConfidence(h.expressions || {});
                    return (
                      <div
                        key={i}
                        style={{
                          ...S.historyItem,
                          borderLeft: `3px solid ${isCorrect ? '#22c55e' : '#ef4444'}`,
                          animation: `fadeSlideUp 0.5s ${1.0 + i * 0.15}s both`,
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#4f8ef7'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = isCorrect ? '#22c55e' : '#ef4444'}
                      >
                        {/* Header row */}
                        <div style={S.historyHeader}>
                          <div style={S.historyQNum}>Q{i + 1}</div>
                          <div style={S.historyQ}>{h.question}</div>
                          <div style={{
                            ...S.resultBadge,
                            ...(isCorrect ? S.badgeCorrect : S.badgeWrong),
                            animation: `starBurst 0.4s ${1.0 + i * 0.15 + 0.3}s both`,
                          }}>
                            {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                          </div>
                        </div>

                        {/* MCQ options */}
                        {h.options ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                            {Object.entries(h.options).map(([key, val]) => {
                              const isRight = key === h.correct;
                              const isWrong = key === h.selected && h.selected !== h.correct;
                              return (
                                <div key={key} style={{
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  padding: '8px 12px', borderRadius: 8, fontSize: 12,
                                  color: isRight ? '#c8c4e0' : isWrong ? '#c8c4e0' : '#8884a0',
                                  background: isRight ? 'rgba(34,197,94,0.08)' : isWrong ? 'rgba(239,68,68,0.08)' : 'transparent',
                                  border: `1px solid ${isRight ? 'rgba(34,197,94,0.25)' : isWrong ? 'rgba(239,68,68,0.25)' : 'transparent'}`,
                                }}>
                                  <span style={{ fontWeight: 700, color: '#4f8ef7', width: 18, flexShrink: 0 }}>{key}.</span>
                                  <span style={{ flex: 1 }}>{val}</span>
                                  {isRight && <span style={{ marginLeft: 'auto', fontSize: 14, color: '#22c55e', fontWeight: 700 }}>✓</span>}
                                  {isWrong && <span style={{ marginLeft: 'auto', fontSize: 14, color: '#ef4444', fontWeight: 700 }}>✗</span>}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: '#c8c4e0', marginBottom: 8, wordBreak: 'break-word' }}>
                            Your answer: {h.transcript}
                          </div>
                        )}

                        {/* AI Feedback bubble */}
                        <div style={S.feedbackBubble}>
                          <strong style={{ color: '#4f8ef7' }}>AI Feedback:</strong> {h.feedback}
                        </div>

                        {/* Confidence bar */}
                        {qConf !== null && (
                          <div style={S.confRow}>
                            <span style={S.confLabel}>Confidence</span>
                            <div style={S.confBar}>
                              <div style={{
                                ...S.confFill,
                                width: confBarsAnimated ? `${Math.round((qConf || 0) * 100)}%` : '0%',
                              }} />
                            </div>
                            <span style={S.confPct}>{Math.round((qConf || 0) * 100)}%</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ── Face Confidence Panel ── */}
                {hasExpressionData && emotionConfidence !== null && (
                  <div style={S.faceConfCard}>
                    <div style={S.faceConfHeader}>
                      <span style={{ fontSize: 22, flexShrink: 0 }}>🎭</span>
                      <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                        <div style={S.faceConfTitle}>Face-Based Confidence Reading</div>
                        <div style={S.faceConfSub}>Analyzed from your expressions across all {history.length} questions</div>
                      </div>
                      <div style={{
                        ...S.faceConfBadge,
                        background: emotionConfidence >= 0.75 ? 'rgba(34,197,94,0.15)' : emotionConfidence >= 0.5 ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                        color: emotionConfidence >= 0.75 ? '#22c55e' : emotionConfidence >= 0.5 ? '#f59e0b' : '#ef4444',
                        border: `1px solid ${emotionConfidence >= 0.75 ? 'rgba(34,197,94,0.3)' : emotionConfidence >= 0.5 ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)'}`,
                      }}>
                        {emotionConfidence >= 0.75 ? '🔥 High' : emotionConfidence >= 0.5 ? '⚡ Moderate' : '💡 Low'}
                      </div>
                    </div>
                    <div style={S.meterWrap}>
                      <div style={S.meterLabelRow}>
                        <span style={{ fontSize: 13, color: '#8884a0' }}>Emotional Confidence Score</span>
                        <span style={{ fontSize: 22, fontWeight: 800, color: emotionConfidence >= 0.75 ? '#22c55e' : emotionConfidence >= 0.5 ? '#f59e0b' : '#ef4444' }}>
                          {Math.round(emotionConfidence * 100)}%
                        </span>
                      </div>
                      <div style={S.meterTrack}>
                        <div style={{
                          ...S.meterFill,
                          width: confBarsAnimated ? `${Math.round(emotionConfidence * 100)}%` : '0%',
                          background: emotionConfidence >= 0.75 ? 'linear-gradient(90deg,#22c55e,#4ade80)' : emotionConfidence >= 0.5 ? 'linear-gradient(90deg,#f59e0b,#fbbf24)' : 'linear-gradient(90deg,#ef4444,#f87171)',
                        }} />
                      </div>
                      <div style={S.meterTicks}><span>Nervous</span><span>Moderate</span><span>Confident</span></div>
                    </div>
                    <div style={{ marginTop: 14 }}>
                      <div style={S.emoBreakdownTitle}>Expression Breakdown (session average)</div>
                      <div style={S.emoGrid}>
                        {Object.entries(overallExpressions).sort((a, b) => b[1] - a[1]).map(([emotion, value]) => (
                          <div key={emotion} style={S.emoItem}>
                            <div style={S.emoTop}>
                              <span style={S.emoName}>{emotionEmoji[emotion] || '😐'} {emotion}</span>
                              <span style={S.emoPct}>{Math.round(value * 100)}%</span>
                            </div>
                            <div style={S.emoTrack}>
                              <div style={{
                                ...S.emoFill,
                                width: confBarsAnimated ? `${Math.round(value * 100)}%` : '0%',
                                background: dominantOverall && emotion === dominantOverall[0] ? '#4f8ef7' : '#2a2840',
                              }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {dominantOverall && (
                      <div style={S.dominantCallout}>
                        <span style={{ color: '#6b6888' }}>Overall dominant expression:</span>
                        <span style={{ color: '#fff', fontWeight: 700, marginLeft: 8 }}>
                          {emotionEmoji[dominantOverall[0]] || '😐'} {dominantOverall[0]}
                          <span style={{ color: '#6b6888', fontWeight: 400, marginLeft: 6 }}>({Math.round(dominantOverall[1] * 100)}%)</span>
                        </span>
                      </div>
                    )}
                    <div style={S.coachingTip}>
                      💡 {emotionConfidence >= 0.75 ? 'Great body language! You appeared calm and engaged throughout the interview.' : emotionConfidence >= 0.5 ? 'Decent composure. Try to smile more and reduce visible tension in future interviews.' : 'You appeared nervous. Practice breathing exercises and mock interviews to build confidence.'}
                    </div>
                  </div>
                )}

                {/* ── CTA row — matches HTML reference: Retry + Back to Home ── */}
                <div style={S.ctaRow}>
                  <button
                    style={S.btnRetry}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f8ef7'; e.currentTarget.style.color = '#4f8ef7'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2840'; e.currentTarget.style.color = '#8884a0'; }}
                    onClick={() => navigate(`/interview/${sectionId}`)}
                  >
                    ↺ Retry Section
                  </button>
                  <button
                    style={S.btnHome}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}
                    onClick={() => navigate('/home')}
                  >
                    ← Back to Home
                  </button>
                </div>
              </div>
            );
          })()}

        </div>
      </div>

      {/* ── All keyframe animations ── */}
      <style>{`
        @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pop { from{transform:translate(-50%,-50%) scale(0)} to{transform:translate(-50%,-50%) scale(1)} }
        @keyframes fadeSlideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%,100%{opacity:0.6} 50%{opacity:1} }
        @keyframes pulseGlow { 0%,100%{box-shadow:0 0 20px rgba(79,142,247,0.2)} 50%{box-shadow:0 0 40px rgba(79,142,247,0.5)} }
        @keyframes ribbonWave { 0%{transform:translateX(-100%) rotate(-8deg)} 100%{transform:translateX(110vw) rotate(-8deg)} }
        @keyframes ribbonWave2 { 0%{transform:translateX(-100%) rotate(5deg)} 100%{transform:translateX(110vw) rotate(5deg)} }
        @keyframes scoreCount { from{opacity:0;transform:scale(0.5)} to{opacity:1;transform:scale(1)} }
        @keyframes starBurst { 0%{transform:scale(0) rotate(0)} 60%{transform:scale(1.2) rotate(20deg)} 100%{transform:scale(1) rotate(0)} }
      `}</style>
    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: '#0f0e1a', color: '#e8e6f0', fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', position: 'relative' },
  confettiCanvas: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 200 },
  ribbonContainer: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'hidden', zIndex: 199 },
  ribbon: { position: 'absolute', height: 18, borderRadius: 9, opacity: 0.7, animationTimingFunction: 'ease-in-out' },

  topbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 28px', background: '#15132a', borderBottom: '1px solid #2a2840' },
  topTitle: { fontSize: 18, fontWeight: 700, color: '#fff' },
  topSub: { fontSize: 12, color: '#6b6888', marginTop: 2 },
  exitBtn: { background: 'transparent', border: '1px solid #3a3858', color: '#8884a0', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  errorBanner: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '10px 20px', fontSize: 13 },
  body: { display: 'flex', flex: 1, gap: 20, padding: 20 },

  leftCol: { width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 },
  camBox: { position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#1a1928', border: '1px solid #2a2840', aspectRatio: '4/3' },
  video: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  camOverlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#4a4868' },
  botSpeakingBadge: { position: 'absolute', bottom: 10, left: 10, background: 'rgba(79,142,247,0.9)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20 },
  listeningBadge: { position: 'absolute', bottom: 10, right: 10, background: 'rgba(239,68,68,0.9)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, animation: 'blink 1.2s infinite' },

  expressionBox: { background: '#1a1928', border: '1px solid #2a2840', borderRadius: 12, padding: 14 },
  expressionTitle: { fontSize: 11, fontWeight: 700, color: '#6b6888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 },
  dominantEmotionText: { fontSize: 20, fontWeight: 700, color: '#22c55e', marginBottom: 10, textTransform: 'capitalize' },
  expRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  expLabel: { fontSize: 11, color: '#8884a0', width: 70, textTransform: 'capitalize' },
  expTrack: { flex: 1, height: 5, background: '#2a2840', borderRadius: 4, overflow: 'hidden' },
  expFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  expPct: { fontSize: 11, color: '#6b6888', width: 30, textAlign: 'right' },

  progressBox: { background: '#1a1928', border: '1px solid #2a2840', borderRadius: 10, padding: 12 },
  progressLabel: { fontSize: 12, color: '#8884a0', marginBottom: 8 },
  progressTrack: { height: 6, background: '#2a2840', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg,#4f8ef7,#22c55e)', borderRadius: 4, transition: 'width 0.5s ease' },

  rightCol: { flex: 1, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', minWidth: 0 },

  setupCard: { background: '#1a1928', border: '1px solid #2a2840', borderRadius: 16, padding: 36, textAlign: 'center' },
  setupTitle: { fontSize: 22, fontWeight: 700, color: '#fff', margin: '0 0 12px' },
  setupDesc: { fontSize: 14, color: '#8884a0', lineHeight: 1.7, marginBottom: 24 },
  setupSteps: { background: '#13121e', borderRadius: 10, padding: '16px 20px', marginBottom: 24, textAlign: 'left' },
  setupStep: { fontSize: 13, color: '#c8c4e0', padding: '6px 0', borderBottom: '1px solid #2a2840' },

  botCard: { background: '#1a1928', border: '1px solid #2a2840', borderRadius: 14, padding: 22 },
  botAvatar: { width: 44, height: 44, background: 'linear-gradient(135deg,#4f8ef7,#7c5fe6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 },
  botName: { fontSize: 15, fontWeight: 700, color: '#fff' },
  botStatus: { fontSize: 12, color: '#6b6888', marginTop: 2 },
  questionBox: {},
  questionLabel: { fontSize: 11, fontWeight: 700, color: '#4f8ef7', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  questionText: { fontSize: 16, color: '#e8e6f0', lineHeight: 1.7, fontWeight: 500 },
  loadingText: { color: '#6b6888', fontSize: 14, fontStyle: 'italic' },

  optionsLabel: { fontSize: 12, color: '#8884a0', marginBottom: 10, fontStyle: 'italic' },
  optionsGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionBtn: { padding: '12px 16px', background: '#13121e', border: '1px solid #2a2840', borderRadius: 10, color: '#c8c4e0', fontSize: 14, textAlign: 'left', cursor: 'pointer', transition: 'all 0.2s ease', lineHeight: 1.5, display: 'block', width: '100%' },
  optionBtnSelected: { background: 'rgba(79,142,247,0.15)', border: '1px solid #4f8ef7', color: '#fff' },
  optionKey: { fontWeight: 700, color: '#4f8ef7', marginRight: 8 },

  transcriptCard: { background: '#1a1928', border: '1px solid #2a2840', borderRadius: 14, padding: 20 },
  transcriptLabel: { fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 },
  liveBadge: { background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, animation: 'blink 1s infinite' },
  transcriptBox: { minHeight: 80, background: '#13121e', borderRadius: 10, padding: 14, fontSize: 14, lineHeight: 1.7, marginBottom: 14, border: '1px solid #2a2840' },
  transcriptActions: { display: 'flex', gap: 10, flexWrap: 'wrap' },

  primaryBtn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg,#4f8ef7,#7c5fe6)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 16 },
  stopBtn: { padding: '9px 18px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  micBtn: { padding: '9px 18px', background: 'rgba(79,142,247,0.15)', border: '1px solid rgba(79,142,247,0.4)', color: '#4f8ef7', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  submitBtn: { padding: '9px 20px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  clearBtn: { padding: '9px 14px', background: 'transparent', border: '1px solid #2a2840', color: '#6b6888', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  nextBtn: { marginTop: 16, padding: '12px 24px', background: 'linear-gradient(135deg,#4f8ef7,#7c5fe6)', border: 'none', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%' },

  feedbackCard: { background: '#1a1928', border: '1px solid rgba(79,142,247,0.3)', borderRadius: 14, padding: 20 },
  feedbackLabel: { fontSize: 13, fontWeight: 700, color: '#4f8ef7', marginBottom: 12 },
  feedbackText: { fontSize: 14, color: '#c8c4e0', lineHeight: 1.7 },

  statusMsg: { textAlign: 'center', fontSize: 13, color: '#6b6888', padding: '8px 0', fontStyle: 'italic' },

  // ── Done phase — exact match to HTML reference ──────────────────
  doneWrapper: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '40px 20px', position: 'relative', width: '100%',
  },

  // Trophy — 120×120 with pop animation + gold glow
  trophyRing: { position: 'relative', width: 120, height: 120, marginBottom: 24 },
  trophyEmoji: {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%,-50%)',
    fontSize: 52,
    animation: 'pop 0.6s cubic-bezier(0.34,1.56,0.64,1) both',
    filter: 'drop-shadow(0 0 20px rgba(250,204,21,0.6))',
  },

  // Title — gradient + fadeSlideUp
  doneTitle: {
    fontSize: 36, fontWeight: 800,
    background: 'linear-gradient(135deg,#fbbf24,#f87171,#a78bfa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
    animation: 'fadeSlideUp 0.7s 0.4s both',
  },
  doneSub: { fontSize: 15, color: '#8884a0', marginTop: 8, animation: 'fadeSlideUp 0.7s 0.6s both' },

  // Score grid — max-width 600px, pulseGlow on each card
  scoreGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14,
    width: '100%', maxWidth: 600, margin: '28px 0',
    animation: 'fadeSlideUp 0.7s 0.8s both',
  },
  scoreCard: {
    background: '#1a1928', border: '1px solid #2a2840', borderRadius: 14,
    padding: '18px 14px', textAlign: 'center',
    animation: 'pulseGlow 3s infinite',
  },
  scoreLabel: { fontSize: 11, color: '#6b6888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 },
  scoreNum: { fontSize: 32, fontWeight: 800, animation: 'scoreCount 0.5s 1.2s both' },

  // History section — max-width 660px
  historySection: { width: '100%', maxWidth: 660, animation: 'fadeSlideUp 0.7s 1.0s both' },
  historyLabel: { fontSize: 12, fontWeight: 700, color: '#4a4868', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 14, textAlign: 'center' },

  // History items — hover handled inline, borderLeft colored, ::before not available in inline styles (done via borderLeft)
  historyItem: {
    background: '#1a1928', border: '1px solid #2a2840', borderRadius: 14,
    padding: '18px 20px', marginBottom: 12, position: 'relative', overflow: 'hidden',
    transition: 'border-color 0.3s',
    boxSizing: 'border-box',
  },
  historyHeader: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 },
  historyQNum: {
    width: 26, height: 26, borderRadius: '50%', background: '#13121e',
    border: '1px solid #2a2840',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, color: '#6b6888', flexShrink: 0, marginTop: 1,
  },
  historyQ: { fontSize: 13, fontWeight: 600, color: '#c8c4e0', lineHeight: 1.5, flex: 1, wordBreak: 'break-word', minWidth: 0 },

  resultBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    flexShrink: 0,
  },
  badgeCorrect: { background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' },
  badgeWrong: { background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' },

  // Feedback bubble
  feedbackBubble: {
    background: '#13121e', borderRadius: 10, padding: '12px 14px',
    fontSize: 12, color: '#8884a0', lineHeight: 1.6, border: '1px solid #2a2840',
    marginBottom: 0, wordBreak: 'break-word',
  },

  // Confidence bar — delayed transition to animate in
  confRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 },
  confLabel: { fontSize: 11, color: '#6b6888', flexShrink: 0 },
  confBar: { flex: 1, height: 4, background: '#2a2840', borderRadius: 4, overflow: 'hidden' },
  confFill: { height: '100%', borderRadius: 4, background: 'linear-gradient(90deg,#4f8ef7,#22c55e)', transition: 'width 1s 1.5s ease' },
  confPct: { fontSize: 11, color: '#22c55e', fontWeight: 700, width: 36, textAlign: 'right' },

  // CTA row — two buttons matching HTML reference
  ctaRow: {
    display: 'flex', gap: 12, marginTop: 28, width: '100%', maxWidth: 660,
    animation: 'fadeSlideUp 0.7s 1.2s both',
  },
  btnHome: {
    flex: 1, padding: 14,
    background: 'linear-gradient(135deg,#4f8ef7,#7c5fe6)',
    color: '#fff', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.15s',
  },
  btnRetry: {
    padding: '14px 24px', background: 'transparent',
    border: '1px solid #2a2840', color: '#8884a0',
    borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer',
    transition: 'border-color 0.2s, color 0.2s',
  },

  // Face confidence panel
  faceConfCard: { background: '#13121e', border: '1px solid #2a2840', borderRadius: 14, padding: 20, marginTop: 16, marginBottom: 4, textAlign: 'left', width: '100%', maxWidth: 660, boxSizing: 'border-box', overflow: 'hidden', minWidth: 0 },
  faceConfHeader: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  faceConfTitle: { fontSize: 14, fontWeight: 700, color: '#fff', wordBreak: 'break-word' },
  faceConfSub: { fontSize: 12, color: '#6b6888', marginTop: 2, wordBreak: 'break-word' },
  faceConfBadge: { padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' },

  meterWrap: { marginBottom: 4, width: '100%' },
  meterLabelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 },
  meterTrack: { height: 8, background: '#2a2840', borderRadius: 6, overflow: 'hidden', marginBottom: 6, width: '100%' },
  meterFill: { height: '100%', borderRadius: 6, transition: 'width 1.2s ease' },
  meterTicks: { display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#4a4868' },

  emoBreakdownTitle: { fontSize: 11, fontWeight: 700, color: '#4a4868', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  emoGrid: { display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 },
  emoItem: { minWidth: 0 },
  emoTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, minWidth: 0 },
  emoName: { fontSize: 12, color: '#8884a0', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  emoPct: { fontSize: 12, color: '#6b6888', fontWeight: 600, flexShrink: 0, marginLeft: 8 },
  emoTrack: { height: 4, background: '#2a2840', borderRadius: 4, overflow: 'hidden', width: '100%' },
  emoFill: { height: '100%', borderRadius: 4, transition: 'width 1s ease' },

  dominantCallout: { marginTop: 14, padding: '9px 12px', background: '#1a1928', borderRadius: 8, fontSize: 13, border: '1px solid #2a2840', wordBreak: 'break-word' },
  coachingTip: { marginTop: 10, padding: '9px 12px', background: 'rgba(79,142,247,0.08)', borderRadius: 8, fontSize: 13, color: '#8884a0', border: '1px solid rgba(79,142,247,0.15)', lineHeight: 1.6, wordBreak: 'break-word' },
};

export default VideoInterviewPage;